import { useNavigate } from "react-router-dom";
import { useContext, useMemo } from "react";
import { Table, Tag } from "antd";

import ContextMenu from "@/components/containers/ContextMenu";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CollapsibleList from "@/components/ui/CollapsibleList";
import CopyableText from "@/components/ui/СopyableText";

import routes from "@/routes";
import { getFullName } from "@/utils";

import { UserContext } from "@/context/UserContext";
import { useColumnConfig } from "@/hooks/useColumnConfig";

const LOCAL_STORAGE_KEY = "employeesTableColumnParameters";

const COLUMN_LABELS = {
  table_number: "Табельный №",
  name: "ФИО",
  email: "Пользователь",
  phone: "Контактные данные",
  units: "Рабочие группы",
  unit: "Подразделение",
  position: "Должность",
  status: "Статус",
};

// Таблица сотрудников
// Содержит список с возможностью сортировки
// Тут можно настроить колонки и ContentMenu
const EmployeesTable = ({
  data,
  positions,
  pagination,
  loading,
  onChange,
  onEditItem,
  onDeleteItem,
  resize = false,
  scroll = { x: "100%" },
}) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const baseColumns = useMemo(
    () =>
      [
        {
          title: COLUMN_LABELS.table_number,
          dataIndex: "table_number",
          key: "table_number",
          width: 130,
          sorter: {
            compare: (a, b) => {
              const num1 = a.employee.table_number ?? Number.MAX_SAFE_INTEGER;
              const num2 = b.employee.table_number ?? Number.MAX_SAFE_INTEGER;

              return num1 - num2;
            },
          },
          render: (_, { employee }) => employee.table_number,
        },
        // Попробуйте удалить defaultSortOrder из столбца "ФИО" и проверьте, работает ли многокритериальная сортировка правильно.
        {
          title: COLUMN_LABELS.name,
          dataIndex: "name",
          key: "name",
          width: 250,
          defaultSortOrder: "ascend",
          sorter: {
            compare: (a, b) => {
              const fioA = getFullName(
                a.employee.lastname,
                a.employee.firstname,
                a.employee.patronymic
              );
              const fioB = getFullName(
                b.employee.lastname,
                b.employee.firstname,
                b.employee.patronymic
              );
              return fioA.localeCompare(fioB);
            },
          },
          ellipsis: { showTitle: true },
          render: (_, { employee }) => (
            <EmployeeProfile
              avatarSize={30}
              href={routes.administration.employeeDetail(employee.uuid)}
              {...employee}
            />
          ),
        },
        {
          title: COLUMN_LABELS.email,
          dataIndex: "email",
          key: "email",
          width: 200,
          ellipsis: { showTitle: true },
          // sorter: {
          //   compare: (a, b) => {
          //     const emailA = a.user?.email;
          //     const emailB = b.user?.email;

          //     return emailA?.localeCompare(emailB);
          //   },
          // },
          render: (_, { user }) =>
            user?.email && <CopyableText text={user.email} hoverable />,
        },
        {
          title: COLUMN_LABELS.phone,
          dataIndex: "phone",
          key: "phone",
          width: 180,
          ellipsis: { showTitle: true },
          render: (_, { employee }) => employee.phone,
        },
        {
          title: COLUMN_LABELS.units,
          dataIndex: "units",
          key: "units",
          width: 300,
          render: (_, { units }) =>
            units ? (
              <CollapsibleList items={units.map((u) => u.name)} count={3} />
            ) : (
              ""
            ),
        },
        {
          title: COLUMN_LABELS.unit,
          dataIndex: "unit",
          key: "unit",
          width: 250,
          ellipsis: { showTitle: true },
          // sorter: {
          //   compare: (a, b) => {
          //     const unitA = units.find(
          //       ({ uuid }) => uuid === a.employee.unit_uuid
          //     )?.name;
          //     const unitB = units.find(
          //       ({ uuid }) => uuid === b.employee.unit_uuid
          //     )?.name;

          //     return unitA?.localeCompare(unitB);
          //   },
          // },
          render: (_, { employee }) => employee.unit?.name || "",
        },
        {
          title: COLUMN_LABELS.position,
          dataIndex: "position",
          key: "position",
          width: 200,
          ellipsis: { showTitle: true },
          // sorter: {
          //   compare: (a, b) => {
          //     const positionA = positions.find(
          //       ({ uuid }) => uuid === a.employee.position_uuid
          //     )?.name;
          //     const positionB = positions.find(
          //       ({ uuid }) => uuid === b.employee.position_uuid
          //     )?.name;

          //     return positionA?.localeCompare(positionB);
          //   },
          //
          // },
          render: (_, { employee }) => {
            const position = positions.find(
              ({ uuid }) => uuid === employee.position_uuid
            );
            return position?.name || "";
          },
        },
        {
          title: COLUMN_LABELS.status,
          dataIndex: "status",
          key: "status",
          width: 110,
          render: (_, { employee }) =>
            employee.dismissaldate ? (
              <Tag color="red">Уволен</Tag>
            ) : (
              <Tag color="green">Работает</Tag>
            ),
        },
        {
          title: "",
          width: 50,
          fixed: "right",
          key: "actions",
          render: (_, { employee }) => (
            <ContextMenu
              vertical
              onView={() =>
                navigate(routes.administration.employeeDetail(employee.uuid))
              }
              onEdit={() => onEditItem(employee)}
              onDelete={() => onDeleteItem(employee)}
            />
          ),
        },
      ].filter(Boolean),
    [navigate, onEditItem, onDeleteItem, positions]
  );

  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels: COLUMN_LABELS,
    }
  );

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={(record) => record.employee.uuid}
      loading={loading}
      pagination={pagination}
      components={resize ? components : undefined}
      onChange={onChange}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      rowClassName="top-align"
    />
  );
};

export default EmployeesTable;
